import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, normalizeClass as _normalizeClass, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8" }
const _hoisted_2 = { class: "flex items-center justify-between mb-3" }
const _hoisted_3 = {
  key: 0,
  class: "h-8 w-16 bg-gray-100 rounded animate-pulse"
}
const _hoisted_4 = {
  key: 1,
  class: "text-3xl font-bold text-gray-900"
}
const _hoisted_5 = { class: "text-sm text-gray-500 mt-1" }
const _hoisted_6 = { class: "bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" }
const _hoisted_7 = { class: "text-sm text-gray-500 mb-6" }
const _hoisted_8 = {
  key: 0,
  class: "h-64 bg-gray-50 rounded-lg animate-pulse"
}
const _hoisted_9 = {
  key: 1,
  class: "h-64 flex flex-col items-center justify-center text-center"
}
const _hoisted_10 = {
  key: 2,
  class: "h-64"
}
const _hoisted_11 = { class: "bg-white rounded-2xl border border-gray-100 shadow-sm p-6" }
const _hoisted_12 = {
  key: 0,
  class: "h-12 bg-gray-50 rounded animate-pulse"
}
const _hoisted_13 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_14 = {
  key: 2,
  class: "space-y-3"
}
const _hoisted_15 = { class: "w-32 text-sm text-gray-600 flex-shrink-0" }
const _hoisted_16 = { class: "flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden" }
const _hoisted_17 = { class: "w-12 text-sm font-semibold text-gray-900 text-right" }

import { ref, computed, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js'
import { apiFor, isEnvironmentUnauthorized } from '../services/api'
import EnvironmentUnavailable from '../components/EnvironmentUnavailable.vue'
import { useModeStore } from '../store/mode'


export default /*@__PURE__*/_defineComponent({
  __name: 'DashboardView',
  setup(__props) {

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler)

const modeStore = useModeStore()

const loading = ref(true)
const envUnavailable = ref(false)
const stats = ref<any>(null)

const cards = computed(() => [
  {
    label: 'Livraisons totales',
    value: stats.value?.totals.deliveries ?? 0,
    icon: 'solar:box-bold',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-700',
  },
  {
    label: 'Livrées',
    value: stats.value?.totals.delivered ?? 0,
    icon: 'solar:check-circle-bold',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
  },
  {
    label: 'En cours',
    value: stats.value?.totals.inProgress ?? 0,
    icon: 'solar:delivery-bold',
    iconBg: 'bg-blue-50',
    iconColor: 'text-blue-600',
  },
  {
    label: 'Annulées',
    value: stats.value?.totals.canceled ?? 0,
    icon: 'solar:close-circle-bold',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-500',
  },
  {
    label: 'Incidents',
    value: stats.value?.totals.incidents ?? 0,
    icon: 'solar:danger-triangle-bold',
    iconBg: 'bg-orange-50',
    iconColor: 'text-orange-600',
  },
])

const hasVolume = computed(() =>
  (stats.value?.dailyVolume || []).some((d: any) => d.count > 0)
)

const chartData = computed(() => ({
  labels: (stats.value?.dailyVolume || []).map((d: any) =>
    new Date(d.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  ),
  datasets: [
    {
      label: 'Livraisons',
      data: (stats.value?.dailyVolume || []).map((d: any) => d.count),
      borderColor: '#8C0004',
      backgroundColor: 'rgba(140, 0, 4, 0.08)',
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 2,
    },
  ],
}))

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: '#f3f4f6' } },
    x: { grid: { display: false }, ticks: { maxTicksLimit: 8 } },
  },
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  CREATED: { label: 'Créées', color: 'bg-gray-400' },
  ASSIGNED: { label: 'Assignées', color: 'bg-blue-500' },
  PICKED_UP: { label: 'En cours', color: 'bg-[#8C0004]' },
  DELIVERED: { label: 'Livrées', color: 'bg-emerald-500' },
  CANCELED: { label: 'Annulées', color: 'bg-gray-300' },
  INCIDENT: { label: 'Incidents', color: 'bg-orange-500' },
}

const statusRows = computed(() => {
  const byStatus = stats.value?.byStatus || {}
  const total = Object.values(byStatus).reduce((a: number, b: any) => a + b, 0) as number
  if (!total) return []
  return Object.entries(byStatus).map(([status, count]) => ({
    status,
    label: STATUS_META[status]?.label || status,
    color: STATUS_META[status]?.color || 'bg-gray-400',
    count: count as number,
    percent: Math.round(((count as number) / total) * 100),
  }))
})

onMounted(loadStats)

async function loadStats() {
  loading.value = true
  try {
    const { data } = await apiFor(modeStore.mode).get('/partner-portal/dashboard/stats')
    stats.value = data
  } catch (err) {
    envUnavailable.value = isEnvironmentUnauthorized(err)
    console.error('Erreur chargement statistiques:', err)
    stats.value = null
  } finally {
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", null, [
    (envUnavailable.value)
      ? (_openBlock(), _createBlock(EnvironmentUnavailable, {
          key: 0,
          class: "mb-6"
        }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cards.value, (card) => {
        return (_openBlock(), _createElementBlock("div", {
          key: card.label,
          class: "bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow"
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("div", {
              class: _normalizeClass(['w-10 h-10 rounded-xl flex items-center justify-center', card.iconBg])
            }, [
              _createVNode(_unref(Icon), {
                icon: card.icon,
                class: _normalizeClass(['w-5 h-5', card.iconColor])
              }, null, 8, ["icon", "class"])
            ], 2)
          ]),
          (loading.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_3))
            : (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(card.value), 1)),
          _createElementVNode("p", _hoisted_5, _toDisplayString(card.label), 1)
        ]))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_6, [
      _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "font-semibold text-gray-900 mb-1" }, "Volume de livraisons", -1)),
      _createElementVNode("p", _hoisted_7, "30 derniers jours — environnement " + _toDisplayString(_unref(modeStore).label), 1),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8))
        : (!hasVolume.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(_unref(Icon), {
                icon: "solar:chart-2-bold",
                class: "w-10 h-10 text-gray-300 mb-3"
              }),
              _cache[1] || (_cache[1] = _createElementVNode("p", { class: "text-gray-500" }, "Aucune livraison sur les 30 derniers jours.", -1)),
              _createVNode(_component_router_link, {
                to: "/docs",
                class: "text-sm text-[#8C0004] hover:underline mt-2"
              }, {
                default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                  _createTextVNode(" Créer votre première livraison depuis la documentation ", -1)
                ]))]),
                _: 1
              })
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_10, [
              _createVNode(_unref(Line), {
                data: chartData.value,
                options: chartOptions
              }, null, 8, ["data"])
            ]))
    ]),
    _createElementVNode("div", _hoisted_11, [
      _cache[3] || (_cache[3] = _createElementVNode("h3", { class: "font-semibold text-gray-900 mb-6" }, "Répartition par statut", -1)),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12))
        : (statusRows.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, " Aucune donnée à afficher. "))
          : (_openBlock(), _createElementBlock("div", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(statusRows.value, (row) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: row.status,
                  class: "flex items-center gap-4"
                }, [
                  _createElementVNode("span", _hoisted_15, _toDisplayString(row.label), 1),
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", {
                      class: _normalizeClass(['h-full rounded-full', row.color]),
                      style: _normalizeStyle({ width: `${row.percent}%` })
                    }, null, 6)
                  ]),
                  _createElementVNode("span", _hoisted_17, _toDisplayString(row.count), 1)
                ]))
              }), 128))
            ]))
    ])
  ]))
}
}

})